import { handleAuthError } from '../utils/authUtils';
import { BaseApiService } from './BaseApiService';

export interface Product {
  id: number;
  name: string;
  product_code: string;
  contains_nuts: boolean;
  sticky_ingredients: boolean;
  shelf_life_days: number | null;
  storage_requirements: 'Honeyware' | 'Cool Room' | 'Warehouse' | 'Chocolate room' | null;
  units_per_case: number;
  packing_speed_units: number | null;
  packing_speed_time: number | null;
  packing_speed_unit: 'seconds' | 'minutes' | 'hours' | null;
  machine_packing_speed_units: number | null;
  machine_packing_speed_time: number | null;
  machine_packing_speed_unit: 'seconds' | 'minutes' | 'hours' | null;
  machine_cases_per_hour?: number | null;
  label_requirements: string;
  auto_inventory_threshold: number | null;
  auto_inventory_ideal: number | null;
  category?: string;
  brand?: string;
  box_size?: string;
  container_name?: string;
  production_line_id?: number | null;
  is_for_production?: boolean;
  auto_create_production_orders?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductBulkRequirement {
  id: number;
  product_id: number;
  bulk_product_id: number;
  bulk_quantity: number;
  finished_quantity: number;
  bulk_product_name: string;
  bulk_product_sku: string;
  // Keep old field for backward compatibility during transition
  cases_needed_per_case?: number;
}

export interface ProductWithBulkRequirements extends Product {
  bulk_requirements: ProductBulkRequirement[];
}

export interface CreateProductData {
  name: string;
  product_code: string;
  contains_nuts: boolean;
  sticky_ingredients: boolean;
  shelf_life_days: number;
  storage_requirements: 'Honeyware' | 'Cool Room' | 'Warehouse' | 'Chocolate room';
  units_per_case: number;
  packing_speed_units: number;
  packing_speed_time: number;
  packing_speed_unit: 'seconds' | 'minutes' | 'hours';
  machine_cases_per_hour?: number;
  label_requirements: string;
  auto_inventory_threshold: number;
  auto_inventory_ideal: number;
  auto_create_production_orders?: boolean;
  category?: string;
  brand?: string;
  box_size?: string;
  container_name?: string;
  production_line_id?: number;
}

export interface UpdateProductData {
  name?: string;
  product_code?: string;
  contains_nuts?: boolean;
  sticky_ingredients?: boolean;
  shelf_life_days?: number;
  storage_requirements?: 'Honeyware' | 'Cool Room' | 'Warehouse' | 'Chocolate room';
  units_per_case?: number;
  packing_speed_units?: number;
  packing_speed_time?: number;
  packing_speed_unit?: 'seconds' | 'minutes' | 'hours';
  machine_cases_per_hour?: number;
  label_requirements?: string;
  auto_inventory_threshold?: number;
  auto_inventory_ideal?: number;
  auto_create_production_orders?: boolean;
  category?: string;
  brand?: string;
  box_size?: string;
  container_name?: string;
  production_line_id?: number;
}

class ProductService extends BaseApiService {
  async getAllProducts(
    includeBulkRequirements = false,
    includeAll = false
  ): Promise<Product[] | ProductWithBulkRequirements[]> {
    try {
      const params: Record<string, string> = {};
      if (includeBulkRequirements) params.includeBulkRequirements = 'true';
      if (includeAll) params.includeAll = 'true';
      const response = await this.get<{
        success: boolean;
        data: Product[] | ProductWithBulkRequirements[];
      }>('/products', params);

      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to fetch products');
      }
    } catch (error: any) {
      if (handleAuthError(error)) return [] as any; // Auth error handled, return empty array
      throw error;
    }
  }

  async getProductsPaginated(
    page = 1,
    pageSize = 20,
    includeBulkRequirements = false,
    includeAll = false
  ): Promise<{
    products: Product[] | ProductWithBulkRequirements[];
    pagination: {
      total: number;
      totalPages: number;
      currentPage: number;
      pageSize: number;
    };
  }> {
    try {
      const params: Record<string, any> = {
        page: page.toString(),
        pageSize: pageSize.toString(),
      };

      if (includeBulkRequirements) {
        params.includeBulkRequirements = 'true';
      }
      if (includeAll) {
        params.includeAll = 'true';
      }

      const response = await this.get<{
        success: boolean;
        data: Product[] | ProductWithBulkRequirements[];
        pagination: {
          total: number;
          totalPages: number;
          currentPage: number;
          pageSize: number;
        };
      }>('/products', params);

      if (response.success) {
        return {
          products: response.data,
          pagination: response.pagination,
        };
      } else {
        throw new Error('Failed to fetch products');
      }
    } catch (error: any) {
      if (handleAuthError(error))
        return {
          products: [] as any,
          pagination: { total: 0, totalPages: 0, currentPage: 1, pageSize: 20 },
        };
      throw error;
    }
  }

  async searchProducts(
    searchTerm: string,
    includeBulkRequirements = false,
    includeAll = false
  ): Promise<Product[] | ProductWithBulkRequirements[]> {
    try {
      const params: Record<string, any> = { search: searchTerm };
      if (includeBulkRequirements) {
        params.includeBulkRequirements = 'true';
      }
      if (includeAll) {
        params.includeAll = 'true';
      }

      const response = await this.get<{
        success: boolean;
        data: Product[] | ProductWithBulkRequirements[];
      }>('/products', params);

      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to search products');
      }
    } catch (error: any) {
      if (handleAuthError(error)) return [] as any;
      throw error;
    }
  }

  async searchProductsPaginated(
    searchTerm: string,
    page = 1,
    pageSize = 20,
    includeBulkRequirements = false,
    includeAll = false
  ): Promise<{
    products: Product[] | ProductWithBulkRequirements[];
    pagination: {
      total: number;
      totalPages: number;
      currentPage: number;
      pageSize: number;
    };
  }> {
    const params: Record<string, any> = {
      search: searchTerm,
      page: page.toString(),
      pageSize: pageSize.toString(),
    };

    if (includeBulkRequirements) {
      params.includeBulkRequirements = 'true';
    }
    if (includeAll) {
      params.includeAll = 'true';
    }

    const response = await this.get<{
      success: boolean;
      data: Product[] | ProductWithBulkRequirements[];
      pagination: {
        total: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      };
    }>('/products', params);

    if (response.success) {
      return {
        products: response.data,
        pagination: response.pagination,
      };
    } else {
      throw new Error('Failed to search products');
    }
  }

  async getProductById(
    id: number,
    includeBulkRequirements = false
  ): Promise<Product | ProductWithBulkRequirements> {
    const params = includeBulkRequirements ? { includeBulkRequirements: 'true' } : {};
    const response = await this.get<{
      success: boolean;
      data: Product | ProductWithBulkRequirements;
    }>(`/products/${id}`, params);

    if (response.success) {
      return response.data;
    } else {
      throw new Error('Failed to fetch product');
    }
  }

  async createProduct(data: CreateProductData): Promise<Product> {
    const response = await this.post<{ success: boolean; data: Product }>('/products', data);

    if (response.success) {
      return response.data;
    } else {
      throw new Error('Failed to create product');
    }
  }

  async updateProduct(id: number, data: UpdateProductData): Promise<Product> {
    const response = await this.put<{ success: boolean; data: Product }>(`/products/${id}`, data);

    if (response.success) {
      return response.data;
    } else {
      throw new Error('Failed to update product');
    }
  }

  async convertToProduction(
    id: number,
    data: {
      shelf_life_days: number;
      storage_requirements: 'Honeyware' | 'Cool Room' | 'Warehouse' | 'Chocolate room';
      packing_speed_units: number;
      packing_speed_time: number;
      packing_speed_unit: 'seconds' | 'minutes' | 'hours';
      production_line_id: number;
      units_per_case?: number;
    }
  ): Promise<{ product: Product; message?: string }> {
    const response = await this.post<{ success: boolean; data: Product; message?: string }>(
      `/products/${id}/convert-to-production`,
      data
    );

    if (response.success) {
      return {
        product: response.data,
        message: response.message,
      };
    } else {
      throw new Error('Failed to convert product to production');
    }
  }

  async deleteProduct(id: number): Promise<void> {
    const response = await this.delete<{ success: boolean; message: string }>(`/products/${id}`);

    if (!response.success) {
      throw new Error('Failed to delete product');
    }
  }

  // Bulk Requirements Management - New Ratio-Based Method
  async addBulkRequirement(
    productId: number,
    bulkProductId: number,
    bulkQuantity: number,
    finishedQuantity: number
  ): Promise<void> {
    const response = await this.post<{ success: boolean; message: string; data?: any }>(
      `/products/${productId}/bulk-requirements`,
      {
        bulk_product_id: bulkProductId,
        bulk_quantity: bulkQuantity,
        finished_quantity: finishedQuantity,
      }
    );

    if (!response.success) {
      throw new Error('Failed to add bulk requirement');
    }
  }

  async removeBulkRequirement(productId: number, bulkProductId: number): Promise<void> {
    const response = await this.delete<{ success: boolean; message: string }>(
      `/products/${productId}/bulk-requirements/${bulkProductId}`
    );

    if (!response.success) {
      throw new Error('Failed to remove bulk requirement');
    }
  }

  // Get unique values for dropdown options
  async getUniqueCategories(): Promise<string[]> {
    const response = await this.get<{ success: boolean; data: string[] }>(
      '/products/unique/categories'
    );
    if (response.success) {
      return response.data;
    } else {
      throw new Error('Failed to fetch categories');
    }
  }

  async getUniqueBrands(): Promise<string[]> {
    const response = await this.get<{ success: boolean; data: string[] }>(
      '/products/unique/brands'
    );
    if (response.success) {
      return response.data;
    } else {
      throw new Error('Failed to fetch brands');
    }
  }

  async getUniqueBoxSizes(): Promise<string[]> {
    const response = await this.get<{ success: boolean; data: string[] }>(
      '/products/unique/box-sizes'
    );
    if (response.success) {
      return response.data;
    } else {
      throw new Error('Failed to fetch box sizes');
    }
  }

  async getUniqueContainerNames(): Promise<string[]> {
    const response = await this.get<{ success: boolean; data: string[] }>(
      '/products/unique/container-names'
    );
    if (response.success) {
      return response.data;
    } else {
      throw new Error('Failed to fetch container names');
    }
  }
}

export const productService = new ProductService();
